/**
 * The lower-third HUD: what the player is doing, what their task is, what they
 * can engage, and the way to everything else.
 *
 * `TN-HUD-hud-and-menu.md` is the acceptance criteria. This file also settles
 * the page's landmarks, which is not a detail — see *Landmarks* below.
 *
 * **Copy.** Everything drawn here is defined in a story table and referenced,
 * never copied: `hud.label`, `hud.menu`, `hud.task`, `common.settings`,
 * `study.open`, `passport.open`, `common.close`, `storage.warning`. The two
 * strings that vary per level — the mode label (`locomotion.<mode>.label`) and
 * the quest step (`quest.step.*`) — arrive as data, because the level and the
 * quest own them.
 *
 * `hud.label`, the accessible name of the region, used to be a required option
 * because no table carried it (`TN-COPY-06`). `TN-HUD` writes it down now, so
 * the region reads the row like every other string it draws, and the option is
 * gone. That is the stronger guarantee, not the weaker one: a caller could not
 * omit the name before, but it could pass "HUD" or "Section", which `TN-HUD`'s
 * table names as defects — and a caller-supplied name could not become French
 * on a language change without a reload, which `TN-HUD-09` requires.
 *
 * **Landmarks.** `TN-HUD-07` requires the page to have exactly one `<main>`,
 * with the `aria-hidden` canvas inside it and `hud` as a named region — and it
 * requires axe's `region` and `landmark-one-main` rules, disabled while the
 * screens were scanned alone over a canvas, to be turned back on. This module
 * builds that page: {@link createHud} creates the `<main>` (once — a second HUD
 * reuses it) and will adopt the canvas host into it when the caller passes one,
 * which is the composition root's decision to make, not this file's.
 *
 * **Input.** The HUD is chrome, not a play control (`OQ-HUD-1`). The region and
 * its rows are `pointer-events: none` and only the controls opt back in, so a
 * hold on the play area behind the strip reaches the level and activates
 * nothing here (`TN-HUD-01`, last scenario).
 *
 * ## One switch, in the level itself
 *
 * `TN-HUD-06` puts the menu in the highlight ring, and a live-site audit found
 * the half nobody had built: **the strip's own controls were in no ring at
 * all**. With one switch the player walked past every landmark — fourteen short
 * presses highlighted nothing, because the only rings on the page belonged to
 * the shell (detached while a level runs, `app/ui/shell.ts`) and to the sheets
 * over the level (`app/ui/screen.ts`), and between two sheets there was nothing.
 *
 * So the strip owns a ring, built by the same {@link createSwitchRing} every
 * other surface uses and with the same contract — short press advances, long
 * press chooses, nothing scans by itself. It walks the strip's **controls** in
 * the order they are drawn (ADR-0039): the offer, then Settings and Menu, then
 * the storage warning's Export when that is raised. The task, the cue, the
 * notice and `interact-hint` are paragraphs and cannot be in it, which
 * `TN-HUD-06` requires and which is a property of what they are rather than a
 * rule applied to them.
 *
 * **Two rings never answer one tap.** A sheet over the level takes the page,
 * exactly as Study takes it from the shell, and the strip stands down:
 * {@link Hud.setCovered} is the same contract `Screen.setCovered` and
 * `Shell.setModalOpen` state, one level along, and the composition root brackets
 * every screen it mounts over the level with it. The HUD brackets its **own**
 * menu, because it owns it.
 *
 * And the strip refuses to come back while it cannot be used: {@link syncRing}
 * never enables a ring over a region that is `inert` — which is what
 * `app/ui/focus-trap.ts` makes the whole page behind an open dialog. Guarded
 * rather than trusted: a caller that forgets to say a sheet opened leaves the
 * player with **no** highlight, which is the failure they can escape by closing
 * the sheet, rather than two rings fighting over one contact.
 *
 * DOM only (ADR-0005): no adapters, no scenes.
 */

import { formatNumber, labelled, text, type UiLocale } from './copy';
import { button, element, replaceChildren } from './dom';
import { createMenu, type Menu } from './menu';
import { injectScreenStyles } from './screen-styles';
import { createSwitchRing, type SwitchRing } from './single-switch';
import { createStorageWarning, type StorageWarning } from './storage-warning';

export interface HudOptions {
  readonly locale: UiLocale;
  /** The one live region (`app/ui/live-region.ts`). */
  readonly announce?: (message: string, lang?: string) => void;
  /** Opening the menu pauses the level (`TN-HUD-02`). */
  readonly onPause?: () => void;
  /** The menu was dismissed without choosing anything: the level resumes. */
  readonly onResume?: () => void;
  readonly onOpenSettings?: () => void;
  readonly onOpenStudy?: () => void;
  readonly onOpenPassport?: () => void;
  /** `flow.leaveLevel`: the route out of the level (`TN-FLOW-03`, `TN-HUD-02`). */
  readonly onLeaveLevel?: () => void;
  /**
   * `about.open`: the "About this place" panel (`docs/content-review.md` §10.2).
   * Absent draws no item — see {@link MenuOptions.onOpenAbout}.
   */
  readonly onOpenAbout?: () => void;
  /** Tapping `interact-prompt` does what tapping the target does (`TN-LEVEL-05`). */
  readonly onInteract?: () => void;
  /** `TN-HUD-03`: the way out the warning has to offer. */
  readonly onExportSave?: () => void;
  readonly singleSwitch?: boolean;
  readonly holdMs?: number;
  readonly now?: () => number;
  /**
   * The element holding the `aria-hidden` canvas. Given one, the HUD moves it
   * inside `<main>` so the page has the structure `TN-HUD-07` describes.
   */
  readonly canvasHost?: HTMLElement;
}

/**
 * Where the task is in its quest: step `number` of `of`, both counted from one.
 *
 * What the bounded indicator draws while an offer holds the strip (ADR-0066 §2):
 * a fixed word and this count, and nothing a content author can lengthen.
 */
export interface TaskPosition {
  readonly number: number;
  readonly of: number;
}

/** How a task line arrives in the strip. */
export interface TaskOptions {
  /**
   * `false` draws the line without saying it.
   *
   * For the task a player **arrived with**: a save with a quest in progress,
   * opened by a reload, Continue or the map. That is the state the level opens
   * in, not a change, and `TN-HUD-07` announces a change. The line is in the
   * accessibility tree as text from the moment it is drawn (`TN-QUEST-08`, "readable
   * at any time, not only when it changes"). Absent or `true`, a new line is
   * announced once, as it always was.
   */
  readonly announce?: boolean;
  /**
   * Which step of how many this task is. Drawn only while an offer is up, as
   * "Task 3/5" in place of the sentence (ADR-0066 §2). Absent draws the word
   * alone, which is still one line and still says a task is running.
   */
  readonly position?: TaskPosition;
}

export interface Hud {
  /** The `hud` region. */
  readonly element: HTMLElement;
  /** The page's one `<main>`. */
  readonly main: HTMLElement;
  readonly menu: Menu;
  /** `interact-prompt`, or `null` when nothing is in reach. */
  readonly prompt: HTMLElement | null;
  /** `hud-mode-label`: "Skating" / « Patinage ». Already localised. */
  setMode(label: string): void;
  /**
   * The task. `null` removes it: there is no task (`TN-HUD-01`). A line that
   * changes is announced once, in full, unless `options.announce` is `false`.
   *
   * **The strip shows one job at a time** (ADR-0066 §2). With nothing in reach
   * the sentence is drawn in full as `hud-quest-tracker`. While an offer is up
   * the offer is drawn in full and the task collapses to `hud-task-indicator`,
   * "Task 3/5", whose accessible name says the same thing expanded for speech
   * ("Task 3 of 5") and never the hidden sentence. The menu draws the sentence
   * in full whenever there is one, whatever is in reach.
   */
  setTask(step: string | null, options?: TaskOptions): void;
  /**
   * `hud-task-cue`: a short label after the task saying where its stop is —
   * today only "Behind you" (second live-site audit: a skipped Town Clock still
   * read "Find the Town Clock" with no hint it was behind the player).
   *
   * Drawn only while there is a task, in the task's own slot and **after** the
   * task line, so the task never moves down for it (ADR-0045). Said once, with
   * the task, when it appears. `null` removes it. The caller decides where the
   * player is; this draws the words.
   */
  setTaskCue(cue: 'behind' | null): void;
  /** `interact-prompt`. `null` withdraws the offer (`TN-LEVEL-05`). */
  setPrompt(label: string | null): void;
  /**
   * `interact-hint`: the one-time explanation of the marks (`TN-REACH-04`).
   *
   * `null` removes it, and removal is permanent in practice because the caller
   * decides it is shown once per sitting. Drawn **beside** the prompt, never
   * instead of it, and as a paragraph rather than a control: it blocks nothing,
   * takes no focus, is not in the Tab order and is not in the switch ring.
   */
  setHint(message: string | null): void;
  /**
   * `hud-notice`: something the game cannot do right now, said in the strip.
   *
   * `TN-QUEST-05` — "the questions are not ready right now" — needs a *visible*
   * sentence as well as an announcement: every sound has a visual equivalent
   * (CLAUDE.md), and a live-region message with nothing on screen is audible to
   * one player and invisible to every other. It is a paragraph and not a dialog,
   * because the story requires the skater to be able to move away: nothing is
   * blocked, nothing takes focus and nothing has to be dismissed.
   *
   * `null` removes it. Already localised — the caller owns the wording, as it
   * does for the tracker and the prompt.
   */
  setNotice(message: string | null): void;
  setStorageWarning(raised: boolean): void;
  /**
   * Put focus in the level, on arrival from the map (`TN-FLOW-06`).
   *
   * A view swap is this game's page navigation, and focus left on a control that
   * has just been detached falls to the body — which is where a keyboard user
   * loses their place and a screen reader goes quiet. The target is the one
   * `<main>`, not the menu button: the player arrived to *play*, and landing
   * them on the way out would make the first Tab press an exit.
   */
  focus(): void;
  openMenu(): void;
  closeMenu(): void;
  setLocale(locale: UiLocale): void;
  /**
   * A surface the *caller* mounted over the level opened or closed.
   *
   * The strip's switch ring stands down while another surface owns the page and
   * comes back when it is gone — the contract `app/ui/screen.ts` states as
   * `setCovered` and `app/ui/shell.ts` as `setModalOpen`, held one level along.
   * Two enabled rings both answer "tap anywhere", and the one under the sheet is
   * the one nobody can see.
   *
   * Focus is untouched: the surface above has just taken it, and its own trap
   * gives it back. The HUD brackets its own menu and needs no call for that.
   */
  setCovered(covered: boolean): void;
  setSingleSwitch(enabled: boolean, holdMs?: number): void;
  destroy(): void;
}

const MAIN_ID = 'tn-main';

/** The attribute the stylesheet places the canvas host by (ADR-0072). */
const CANVAS_ATTRIBUTE = 'data-tn-canvas';

export function createHud(host: HTMLElement, options: HudOptions): Hud {
  const doc = host.ownerDocument;
  injectScreenStyles(doc);

  let locale = options.locale;
  let mode = '';
  let task: string | null = null;
  let taskPosition: TaskPosition | null = null;
  let promptLabel: string | null = null;
  let hint: string | null = null;
  let notice: string | null = null;
  let taskCue: 'behind' | null = null;
  let singleSwitch = options.singleSwitch === true;
  let holdToChooseMs = options.holdMs ?? 600;
  /** A screen the caller mounted over the level is up. See {@link Hud.setCovered}. */
  let covered = false;

  const main = findOrCreateMain(doc, host);
  /*
   * The canvas belongs inside the one `<main>`, and it is `aria-hidden`, so it
   * contributes nothing to the accessibility tree either way — what it changes
   * is that `<main>` is real content rather than a wrapper invented to satisfy a
   * scanner. Whether to hand it over is the composition root's call.
   */
  if (options.canvasHost !== undefined && options.canvasHost.parentElement !== main) {
    main.append(options.canvasHost);
  }
  /*
   * Named for the stylesheet, so that with the dyslexia face on the canvas can
   * sit at the top of its space and the taller strip stays below the player
   * (ADR-0072). An attribute and nothing else: the HUD never measures or moves
   * the canvas itself, and the engine refits it to its host as it always does.
   */
  options.canvasHost?.setAttribute(CANVAS_ATTRIBUTE, 'level');

  const modeLabel = element(doc, 'p', {
    testId: 'hud-mode-label',
    className: 'tn-hud__mode',
  });
  /*
   * `TN-MOVE-02`: the strip is "either absent from the accessibility tree or has
   * non-empty text", and "never present with an empty string". Until a level
   * says how the player moves there is nothing to say, and an empty paragraph is
   * the worst way to say it: no box for a sighted player, nothing at all for a
   * screen reader, and a `toBeVisible` assertion that fails on a missing word
   * rather than on a missing element. Hidden until {@link Hud.setMode}, never
   * filled with the mode's id, "Mode" or a dash.
   */
  modeLabel.hidden = true;

  /* The mode row: text only, no `aria-live` of its own (`TN-HUD-07`). A change
     is announced once, through the one live region. The task has its own slot
     further up (ADR-0045). */
  const status = element(doc, 'div', {
    className: 'tn-hud__status',
    children: [modeLabel],
  });

  /* The task has a slot of its own, directly under Settings and Menu (ADR-0045):
     at 200 % text only the top of the strip is on screen, and the task is the
     line a player acts on, so it is never below a paragraph that explains
     something else. */
  const taskSlot = element(doc, 'div', { className: 'tn-hud__slot' });
  const warningSlot = element(doc, 'div', { className: 'tn-hud__slot' });
  /* The hint has a slot of its own, after the prompt's and the controls, rather
     than sharing one: the offer is what the player can act on, the hint explains
     the marks, and two slots keep that order however either of them arrives. */
  const hintSlot = element(doc, 'div', { className: 'tn-hud__slot' });
  const noticeSlot = element(doc, 'div', { className: 'tn-hud__slot' });
  const promptSlot = element(doc, 'div', { className: 'tn-hud__slot' });

  /*
   * Settings, on the strip, beside Menu.
   *
   * It was reachable before this — Menu, then "Settings" — and it was reported
   * as unreachable, which is the same defect this project keeps finding: a
   * capability that exists and is not *offered*. `TN-SET-01` asks for settings
   * "from the game", `TN-HUD-02` keeps the menu's item, and a player who does
   * not know a menu contains it has to open the menu to find out. Two controls
   * on the strip is the cheapest way to make the answer visible, and it costs
   * the menu nothing: the item stays, both routes open the same screen, and
   * closing it returns focus to whichever control opened it.
   *
   * Drawn only when the caller wired one, like every other item here: a Settings
   * button that opens nothing is worse than no button.
   */
  const settingsButton = button(doc, {
    testId: 'hud-settings-button',
    className: 'tn-hud__settings-button',
    text: text(locale, 'common.settings'),
    ...(options.onOpenSettings === undefined
      ? {}
      : {
          onClick: (): void => {
            openScreen(options.onOpenSettings);
          },
        }),
  });
  settingsButton.hidden = options.onOpenSettings === undefined;

  const menuButton = button(doc, {
    testId: 'menu-button',
    className: 'tn-hud__menu-button',
    text: text(locale, 'hud.menu'),
    onClick: () => openMenu(),
  });

  /* One row, wrapping to two when the text is large enough that two words do
     not share a 390 px line. */
  const controls = element(doc, 'div', {
    className: 'tn-hud__controls',
    children: [settingsButton, menuButton],
  });

  /*
   * `<section>` with an accessible name is a `region` landmark, which is what
   * `TN-HUD-07` asks for by name. Every piece of visible text the HUD draws is
   * inside it, so axe's `region` rule has nothing left outside a landmark.
   *
   * **What the player can do comes first, and what explains it after**
   * (ADR-0039). The strip is capped at a third of the viewport and scrolls inside
   * itself, so whatever is first is what is on screen. At 200 % text on a
   * 390 x 844 phone the old order — mode, task, warning, notice, hint, then the
   * prompt and Menu — put "Talk to the guide" below the fold behind a hint that
   * filled the strip, and cut Menu off at spawn. Now the offer and the two
   * controls are always the top of the strip, and the paragraphs below them
   * scroll. The DOM order *is* the visual order, so reading order and focus
   * order agree with what a sighted player sees (WCAG 1.3.2, 2.4.3).
   *
   * **The task comes straight after them** (ADR-0045). It used to follow the
   * warning, the notice and the mode, and at 200 % text in French on Halifax it
   * ended 63 px below the screen. The task is what the player is acting on; the
   * mode, the notice and the hint are what may scroll.
   *
   * One scroll box and not two: a strip with a text-only scroll area inside it
   * would be a scrollable region with nothing focusable (axe's
   * `scrollable-region-focusable`), and a pinned footer over scrolling text would
   * leave axe unable to compute that text's contrast.
   */
  const region = element(doc, 'section', {
    testId: 'hud',
    className: 'tn-hud',
    attrs: { 'aria-label': text(locale, 'hud.label') },
    children: [promptSlot, controls, taskSlot, warningSlot, noticeSlot, status, hintSlot],
  });
  main.append(region);

  const warning: StorageWarning = createStorageWarning(warningSlot, {
    locale,
    ...(options.announce === undefined ? {} : { announce: options.announce }),
    ...(options.onExportSave === undefined ? {} : { onExport: options.onExportSave }),
  });

  /*
   * The menu is mounted on `host`, beside `<main>` rather than inside it. That
   * is what lets its focus trap make the whole page — HUD included — inert while
   * it is open, which is `TN-HUD-04`'s "menu-button is not reachable with Tab"
   * and "tapping where menu-button is does not open the menu", with no state of
   * our own to keep in step.
   */
  const menu: Menu = createMenu(host, {
    locale,
    ...(options.announce === undefined ? {} : { announce: options.announce }),
    /*
     * Every item is wrapped, and the wrapper is one line: the screen it opens
     * takes the page, so the strip re-reads its own ring and stands down. The
     * menu closes itself before the handler runs (`app/ui/menu.ts`), so without
     * this the strip would come back for exactly as long as it takes the next
     * screen to open — and in a caller that never brackets it, for good.
     */
    ...(options.onOpenSettings === undefined
      ? {}
      : {
          onOpenSettings: (): void => {
            openScreen(options.onOpenSettings);
          },
        }),
    ...(options.onOpenStudy === undefined
      ? {}
      : {
          onOpenStudy: (): void => {
            openScreen(options.onOpenStudy);
          },
        }),
    ...(options.onOpenPassport === undefined
      ? {}
      : {
          onOpenPassport: (): void => {
            openScreen(options.onOpenPassport);
          },
        }),
    ...(options.onLeaveLevel === undefined
      ? {}
      : {
          onLeaveLevel: (): void => {
            openScreen(options.onLeaveLevel);
          },
        }),
    ...(options.onOpenAbout === undefined
      ? {}
      : {
          onOpenAbout: (): void => {
            openScreen(options.onOpenAbout);
          },
        }),
    onDismiss: () => {
      options.onResume?.();
      /* Nothing was chosen and the level is the player's again: the strip takes
         the contact back. */
      syncRing();
    },
    singleSwitch: options.singleSwitch === true,
    holdMs: options.holdMs ?? 600,
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  /**
   * The strip's ring: its controls, in the order they are drawn.
   *
   * Built over the region rather than over `<main>`, so the `aria-hidden` canvas
   * and anything the composition root mounts beside the strip are outside it —
   * the ring walks what the player can press in the level, and nothing else. The
   * default selector is the shared one, which is why `interact-hint`, the task,
   * the cue and the notice cannot appear in it: they are paragraphs.
   */
  const ring: SwitchRing = createSwitchRing(region, {
    /* Read at each press, so a hold time changed in Settings applies to the
       strip without the level being reopened (`TN-SET-09`). */
    holdMs: () => holdToChooseMs,
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.announce === undefined
      ? {}
      : {
          announce: (message: string): void => {
            say(message);
          },
        }),
  });

  /** Is another surface answering "tap anywhere" right now? */
  function ringIsCovered(): boolean {
    if (covered || menu.visible) return true;
    /*
     * The belt to that brace. A focus trap makes everything behind an open
     * dialog `inert` (`app/ui/focus-trap.ts`), so a strip that cannot be pressed
     * is a strip that must not be scanned — whether or not the caller remembered
     * to say a screen had opened. A detached strip is the same answer: the level
     * has been left.
     */
    return !region.isConnected || region.closest('[inert]') !== null;
  }

  function syncRing(): void {
    if (!singleSwitch || ringIsCovered()) {
      ring.disable();
      return;
    }
    if (!ring.enabled) ring.enable();
    ring.refresh();
    if (ring.index !== -1) return;
    /*
     * Where the highlight opens, and it is not always item one.
     *
     * `TN-FLOW-07` says it starts at the first item and does not move until the
     * player presses. In the strip the first item is the **offer** when there is
     * one, which is what ADR-0039 already puts first on screen — so a switch
     * player who has walked up to the guide is one long press from talking to
     * them. Focus wins where it is already on a control in the strip, so a
     * player who arrived there with Tab is not sent back to the top.
     */
    const focused = doc.activeElement as HTMLElement | null;
    const at = focused === null ? -1 : ring.items.indexOf(focused);
    ring.highlight(at === -1 ? 0 : at);
  }

  /**
   * A control in the strip — or an item in the menu — opened something.
   *
   * The screen that opens owns the page from here, so the ring is re-read after
   * the handler rather than before it: by then the new surface has activated its
   * trap, {@link ringIsCovered} can see it, and the strip has stood down.
   */
  function openScreen(open: (() => void) | undefined): void {
    open?.();
    syncRing();
  }

  function openMenu(): void {
    menu.open();
    options.onPause?.();
    syncRing();
  }

  /* The strip may open with the ring already live: single-switch is a saved
     setting, so a player who enters a level with it on has a highlight before
     they press anything (`TN-FLOW-07`). */
  syncRing();

  function say(message: string): void {
    options.announce?.(message, locale);
  }

  /**
   * The task row: the sentence in full, or the bounded indicator while an offer
   * is up (ADR-0066 §2). Never both, and the menu's line is kept in step here
   * because it is the task's permanent home.
   *
   * Removed, not hidden, whichever of the two is not drawn: `TN-HUD-01`, "the
   * tracker appears only when there is a task". A hidden tracker is one
   * stylesheet away from being visible and one screen reader away from being
   * read.
   */
  function renderTask(): void {
    menu.setTask(task);
    const existing = trackerElement();
    const indicator = indicatorElement();
    if (task === null) {
      existing?.remove();
      indicator?.remove();
      return;
    }
    if (promptLabel !== null) {
      existing?.remove();
      renderIndicator(indicator);
      return;
    }
    indicator?.remove();
    const wording = labelled(locale, text(locale, 'hud.task'), task);
    if (existing !== null) {
      existing.textContent = wording;
      return;
    }
    /* First in the slot, so the cue, when there is one, follows it. */
    taskSlot.prepend(
      element(doc, 'p', {
        testId: 'hud-quest-tracker',
        className: 'tn-hud__task',
        text: wording,
      }),
    );
  }

  /**
   * "Task 3/5" / « Mission 3/5 »: a fixed word and a count, one line in either
   * language, and nothing in it comes from `content/` (ADR-0066 §2).
   *
   * Two spans rather than an `aria-label`. The visible words are hidden from
   * assistive technology and a visually hidden twin says them expanded for
   * speech — "Task 3 of 5" — because "3/5" is read aloud as a date or a fraction
   * depending on the reader. Both say the same thing; neither carries the task
   * sentence, which a sighted player cannot see here either (ADR-0066 §2, TN-REACH:
   * the same information for everybody). An `aria-label` on a paragraph would
   * also be a name on a role that prohibits one.
   */
  function renderIndicator(existing: HTMLElement | null): void {
    const shown =
      taskPosition === null
        ? text(locale, 'hud.task')
        : text(locale, 'hud.task.indicator', {
            n: formatNumber(locale, taskPosition.number),
            total: formatNumber(locale, taskPosition.of),
          });
    const spoken =
      taskPosition === null
        ? text(locale, 'hud.task')
        : text(locale, 'hud.task.indicator.spoken', {
            n: formatNumber(locale, taskPosition.number),
            total: formatNumber(locale, taskPosition.of),
          });
    const visible = element(doc, 'span', {
      testId: 'hud-task-indicator-text',
      text: shown,
      attrs: { 'aria-hidden': 'true' },
    });
    const name = element(doc, 'span', {
      testId: 'hud-task-indicator-name',
      className: 'tn-hud__spoken',
      text: spoken,
    });
    if (existing !== null) {
      replaceChildren(existing, [visible, name]);
      return;
    }
    taskSlot.prepend(
      element(doc, 'p', {
        testId: 'hud-task-indicator',
        className: 'tn-hud__task tn-hud__task--indicator',
        children: [visible, name],
      }),
    );
  }

  function trackerElement(): HTMLElement | null {
    return taskSlot.querySelector<HTMLElement>('[data-testid="hud-quest-tracker"]');
  }

  function indicatorElement(): HTMLElement | null {
    return taskSlot.querySelector<HTMLElement>('[data-testid="hud-task-indicator"]');
  }

  /**
   * The cue after the task. Removed, not hidden, whenever there is no task or no
   * cue, for the tracker's reason. Appended after the tracker, so it follows the
   * task line in reading order and on screen.
   */
  function renderTaskCue(): void {
    const existing = taskSlot.querySelector<HTMLElement>('[data-testid="hud-task-cue"]');
    /* The cue belongs to the full row and is not drawn beside the indicator
       (ADR-0066 §2). It was said once when it became true, and that stands. */
    if (task === null || taskCue === null || promptLabel !== null) {
      existing?.remove();
      return;
    }
    const words = text(locale, 'hud.task.behind');
    if (existing !== null) {
      existing.textContent = words;
      return;
    }
    taskSlot.append(
      element(doc, 'p', {
        testId: 'hud-task-cue',
        className: 'tn-hud__cue',
        attrs: { 'data-cue': taskCue },
        text: words,
      }),
    );
  }

  function renderPrompt(): void {
    const existing = promptElement();
    if (promptLabel === null) {
      existing?.remove();
      return;
    }
    if (existing !== null) {
      existing.textContent = promptLabel;
      return;
    }
    promptSlot.append(
      button(doc, {
        testId: 'interact-prompt',
        className: 'tn-hud__prompt',
        text: promptLabel,
        ...(options.onInteract === undefined
          ? {}
          : {
              onClick: (): void => {
                openScreen(options.onInteract);
              },
            }),
      }),
    );
    /* A new offer is the top of the strip, and a player who had scrolled down to
       read the hint must not have it arrive out of sight (ADR-0039). */
    region.scrollTop = 0;
  }

  /**
   * The hint, below the offer and the controls.
   *
   * Removed rather than hidden, for the reason the tracker is: `TN-REACH-04`
   * requires it to be absent from the accessibility tree once the player has
   * engaged anything, and a hidden element is one stylesheet away from being
   * visible and one screen reader away from being read.
   *
   * It has a slot of its own after the prompt's, so the offer is never pushed
   * out of the strip by the sentence explaining it (ADR-0039) — it used to sit
   * above the prompt, and at 200 % text it filled the strip.
   */
  function renderHint(): void {
    const existing = hintElement();
    if (hint === null) {
      existing?.remove();
      return;
    }
    if (existing !== null) {
      existing.textContent = hint;
      return;
    }
    hintSlot.append(
      element(doc, 'p', {
        testId: 'interact-hint',
        className: 'tn-hud__hint',
        text: hint,
      }),
    );
  }

  function hintElement(): HTMLElement | null {
    return hintSlot.querySelector<HTMLElement>('[data-testid="interact-hint"]');
  }

  function renderNotice(): void {
    const existing = noticeSlot.querySelector<HTMLElement>('[data-testid="hud-notice"]');
    if (notice === null) {
      existing?.remove();
      return;
    }
    if (existing !== null) {
      existing.textContent = notice;
      return;
    }
    noticeSlot.append(
      element(doc, 'p', {
        testId: 'hud-notice',
        className: 'tn-hud__notice',
        text: notice,
      }),
    );
  }

  function promptElement(): HTMLElement | null {
    return promptSlot.querySelector<HTMLElement>('[data-testid="interact-prompt"]');
  }

  return {
    element: region,
    main,
    menu,
    get prompt(): HTMLElement | null {
      return promptElement();
    },

    setMode(label): void {
      if (label === mode) return;
      /* The first value is the state the player arrived in, not a change, and
         the arrival announcement (`announce.arrived.ottawa`) already carries it:
         "You are on the Rideau Canal in Ottawa. Skating." Speaking it again here
         would be the same word twice, from two speakers neither of which knows
         about the other. A later change — skate to walk — is news and is said. */
      const first = mode === '';
      mode = label;
      modeLabel.textContent = label;
      modeLabel.hidden = label === '';
      if (!first) say(label);
    },

    setTask(step, options): void {
      const position = step === null ? null : (options?.position ?? null);
      const moved =
        position?.number !== taskPosition?.number || position?.of !== taskPosition?.of;
      if (step === task && !moved) return;
      const changed = step !== task;
      task = step;
      taskPosition = position;
      renderTask();
      renderTaskCue();
      if (changed && step !== null && options?.announce !== false) {
        say(labelled(locale, text(locale, 'hud.task'), step));
      }
    },

    setTaskCue(cue): void {
      if (cue === taskCue) return;
      const appeared = taskCue === null && cue !== null;
      taskCue = cue;
      renderTaskCue();
      /* "Behind you: Find the Town Clock", once, when it starts to be true. Not
         when it stops: the player walking back to the stop is the news, and the
         prompt there says so. */
      if (appeared && task !== null) say(labelled(locale, text(locale, 'hud.task.behind'), task));
    },

    setPrompt(label): void {
      if (label === promptLabel) return;
      promptLabel = label;
      renderPrompt();
      /* The offer and the task take turns (ADR-0066 §2): an offer arriving
         collapses the task to its indicator, and one withdrawn gives the
         sentence back, cue and all. Drawn, not said — the task has not changed. */
      renderTask();
      renderTaskCue();
      /* The offer arriving or being withdrawn changes the ring, and it is the
         one item in it that comes and goes. An offer that appeared while the
         highlight was on Menu leaves it on Menu: `refresh` follows the item, not
         the index. */
      syncRing();
      /* Deliberately silent. `app/ui/level-events.ts` announces the offer when
         the target comes into reach; announcing here as well would say it
         twice, and neither speaker would know the other had. */
    },

    setHint(message): void {
      if (message === hint) return;
      hint = message;
      renderHint();
      /* Deliberately silent, like `setPrompt`: `TN-REACH-04` allows the hint to
         be announced at most once, and the caller is the only thing that knows
         whether this is that once. */
    },

    setNotice(message): void {
      if (message === notice) return;
      notice = message;
      renderNotice();
      /* Deliberately silent: the caller announces it once, at the moment the
         thing it is about happened, and a second speaker here would say it
         twice. */
    },

    setStorageWarning(raised): void {
      if (raised) warning.raise();
      else warning.clear();
      /* The warning is words, but the way out of it is a control (`TN-HUD-03`),
         and `TN-HUD-06` requires every real control to stay reachable. */
      syncRing();
    },

    focus(): void {
      main.focus();
    },

    openMenu,

    closeMenu(): void {
      menu.close();
    },

    setLocale(next): void {
      locale = next;
      region.setAttribute('lang', next);
      /* `TN-HUD-09`: the region's name changes with the language, without the
         level reloading. */
      region.setAttribute('aria-label', text(next, 'hud.label'));
      menuButton.textContent = text(next, 'hud.menu');
      settingsButton.textContent = text(next, 'common.settings');
      renderTask();
      renderTaskCue();
      /* The hint is the caller's string in the caller's language, so it is
         re-supplied rather than translated here; what this does is keep whatever
         is on screen consistent when the strip is redrawn. */
      renderHint();
      renderNotice();
      warning.setLocale(next);
      menu.setLocale(next);
      /* The words a long press would choose have changed, so what the ring
         announces has to change with them. */
      syncRing();
    },

    setCovered(next): void {
      if (covered === next) return;
      covered = next;
      syncRing();
    },

    setSingleSwitch(enabled, holdMs): void {
      singleSwitch = enabled;
      if (holdMs !== undefined) holdToChooseMs = holdMs;
      menu.setSingleSwitch(enabled, holdMs);
      syncRing();
    },

    destroy(): void {
      ring.destroy();
      menu.destroy();
      warning.destroy();
      region.remove();
      options.canvasHost?.removeAttribute(CANVAS_ATTRIBUTE);
    },
  };
}

/**
 * One `<main>` per page (`landmark-one-main`). A second HUD — a level reloading
 * over an old one — finds the first rather than adding a landmark that makes the
 * rule fail.
 */
function findOrCreateMain(doc: Document, host: HTMLElement): HTMLElement {
  const existing = doc.getElementById(MAIN_ID);
  if (existing !== null) return existing;

  const main = element(doc, 'main', { id: MAIN_ID, className: 'tn-main' });
  /* Focusable programmatically, never in the Tab order: `Hud.focus` moves focus
     here when a level takes the page, and a landmark that answered Tab would put
     a stop before every control on the way to the first one. */
  main.tabIndex = -1;
  host.append(main);
  return main;
}
